import Phaser from 'phaser';
import { gameAudio } from '../audio/audio';
import { haptics } from '../../platform/haptics';
import { reducedMotion } from '../ui/motion';
import { HUD_HEIGHT } from '../layout';
import { bevelButton, drawBigStar, navyPanel } from '../ui/chrome';
import { label, UI } from '../ui/text';
import { wrapLineCount } from '../ui/wrap';
import { drawCoin } from '../ui/widgets';
import type { StarGoals } from '../starGoals';
// The three stars (par, all coins, the challenge), as everywhere else; `clear` is not a star.
import { STAR_BITS } from '../../meta/save/schema';
import type { HudScene } from './HudScene';

export interface ResultsModel {
  /** A small line over the title naming the lawn ("Lawn 27: Tidy Acre"). */
  lawn?: string;
  title: string;
  titleColor?: number;
  /** Star bits earned and which of them are new (won attempts only). */
  stars?: { bits: number; fresh: number };
  /** What each star asks (starGoals): a GOALS button beside the stars shows them. */
  goals?: StarGoals['goals'];
  /** Plain info lines (time, % mowed, ...). */
  info: string[];
  /**
   * Online: a line under the info that fills in once the run's place comes back from the server
   * ("#12 of 340 today"; null leaves it empty), and opens that board when tapped.
   */
  rank?: { load: Promise<string | null>; open?: () => void };
  /** Itemized coin rewards. */
  coinLines: { label: string; coins: number }[];
  /** Wallet after the payout. */
  wallet?: number;
  note?: string;
  /** A ribbon under the title, e.g. NEW BEST TIME! */
  banner?: string;
  buttons: {
    label: string;
    action: () => void;
    primary?: boolean;
    /** Where it goes, when that is another lawn: the Garage can offer the same after a reveal. */
    goes?: 'next' | 'retry';
  }[];
  /** A win: the title stamps in big and confetti falls. */
  cheer?: boolean;
  /** Lawn Art: the three judges' scores (1-10), raised one after another. */
  judges?: readonly number[];
  /**
   * Coins earned fly from each payout line into the HUD's coin counter (screen pixels), which
   * counts up from `from` as they land (`onLand` with the counter's value; null when done).
   */
  coinFlight?: {
    from: number;
    target: { x: number; y: number };
    onLand: (wallet: number | null) => void;
  };
  /**
   * A paid option shown in its own box above the buttons (continue after failing): never the
   * default action, and the price is shown with the coin icon.
   */
  offer?: {
    title: string;
    detail?: string;
    cost: number;
    /** Null when the wallet is short: the box says what it costs instead of offering a button. */
    action: (() => void) | null;
  };
}

const PANEL_W = 184;
/** Text keeps this far inside the panel's edge (its frame is 3 px). */
const INSET = 8;
const LINE = 11;
const STAR_SCALE = 3;
const STAR_SIZE = 7 * STAR_SCALE;
const BUTTON_H = 20;
const PRIMARY_H = 26;
const ROLL_MS = 600;

/**
 * End-of-attempt overlay (over the lawn and HUD) in the navy UI material (docs/ART_DIRECTION.md):
 * title, big stars that stamp in one by one, payout and next steps. The primary action is the
 * wide button at the bottom (thumb reach); the others sit side by side above it.
 */
export class ResultsScene extends Phaser.Scene {
  /** Where the panel's top sits on screen (the camera scrolls the panel there). */
  private panelTop = 0;

  constructor() {
    super('Results');
  }

  create(model: ResultsModel): void {
    const { width, height } = this.scale.gameSize;
    const x0 = Math.round((width - PANEL_W) / 2);
    const g = this.add.graphics();
    const parts: (() => void)[] = [];
    // Below the panel's frame.
    let y = 8;
    const line = (fn: (top: number) => void, h = LINE) => {
      const top = y;
      parts.push(() => fn(top));
      y += h;
    };

    const textW = PANEL_W - 2 * INSET;
    if (model.lawn) {
      const lawn = model.lawn;
      line(
        (t) =>
          label(this, width / 2, t, lawn, 1, UI.dim)
            .setMaxWidth(textW)
            .setCenterAlign()
            .setOrigin(0.5, 0),
        wrapLineCount(lawn, textW) * 10 + 2,
      );
    }
    // Big title when it fits on one line, else body size, wrapped and centered.
    const title = model.title.toUpperCase();
    const titleScale = title.length * 12 - 2 <= textW ? 2 : 1;
    const titleLines = titleScale === 2 ? 1 : wrapLineCount(title, textW);
    line(
      (t) => {
        const text = label(this, width / 2, t, title, titleScale, model.titleColor ?? UI.accent)
          .setMaxWidth(textW)
          .setCenterAlign()
          .setOrigin(0.5, 0);
        if (model.cheer) this.stampTitle(text);
      },
      titleScale === 2 ? 22 : titleLines * 10 + 4,
    );
    if (model.banner) {
      const banner = model.banner;
      line((t) => this.drawBanner(width / 2, t, banner), 16);
    }
    if (model.stars) {
      const stars = model.stars;
      const goals = model.goals;
      line((t) => {
        this.drawStars(width / 2, t, stars.bits, stars.fresh);
        if (goals) this.goalsButton(x0, t, goals, stars.bits);
      }, STAR_SIZE + 8);
    }
    if (model.judges) {
      const scores = model.judges;
      line((t) => this.drawJudges(width / 2, t, scores), 34);
    }
    for (const info of model.info) {
      line(
        (t) =>
          label(this, width / 2, t, info)
            .setMaxWidth(textW)
            .setCenterAlign()
            .setOrigin(0.5, 0),
        wrapLineCount(info, textW) * 10 + 1,
      );
    }
    if (model.rank) {
      const rank = model.rank;
      // With a board to open, the place is a button (owner, 2026-11-04: plain text did not read as
      // one); otherwise plain text.
      const RANK_H = 14;
      line(
        (t) => {
          // (Gone with the scene if it closed before the board answered.)
          const anchor = this.add.zone(0, t, 1, 1);
          void rank.load.then((place) => {
            if (!place || !anchor.scene) return;
            const open = rank.open;
            if (!open) {
              label(this, width / 2, t + 1, place, 1, UI.accent).setOrigin(0.5, 0);
              return;
            }
            const maxW = PANEL_W - 12;
            const withArrow = `${place} >`;
            const text = withArrow.length * 6 - 1 + 12 <= maxW ? withArrow : place;
            const w = Math.min(maxW, text.length * 6 - 1 + 12);
            bevelButton(this, Math.round(width / 2 - w / 2), t, w, RANK_H, text, open).setData(
              'label',
              'RANK',
            );
          });
        },
        rank.open ? RANK_H + 4 : LINE + 1,
      );
    }
    // Coins roll up from zero once the stars have stamped in.
    const rollDelay = model.stars ? 200 + STAR_BITS.length * 220 + 150 : 250;
    const earned = model.coinLines.reduce((sum, c) => sum + Math.max(0, c.coins), 0);
    // What the wallet actually changed by (penalties included; a payout never goes below zero).
    const net = Math.max(
      0,
      model.coinLines.reduce((sum, c) => sum + c.coins, 0),
    );
    // Where each payout line's value sits, for the coins that fly from it.
    const lineSpots: { y: number; coins: number }[] = [];
    if (model.coinLines.length > 0) {
      y += 4;
      for (const c of model.coinLines) {
        line((t) => {
          lineSpots.push({ y: t + 3, coins: c.coins });
          label(this, x0 + 12, t, c.label, 1, UI.dim);
          const sign = c.coins >= 0 ? '+' : '';
          const text = label(
            this,
            x0 + PANEL_W - 12,
            t,
            `${sign}0`,
            1,
            c.coins >= 0 ? UI.accent : UI.danger,
          ).setOrigin(1, 0);
          this.roll(text, 0, c.coins, rollDelay, (v) => `${sign}${v}`);
        });
      }
    }
    if (model.wallet !== undefined) {
      const wallet = model.wallet;
      line((t) => {
        // Place the coin icon for the final (widest) value, then roll up to it.
        const text = label(this, x0 + PANEL_W - 12, t, `Wallet ${wallet}`, 1, UI.text);
        text.setOrigin(1, 0);
        drawCoin(g, Math.round(text.x - text.width - 10), t);
        this.roll(text, wallet - net, wallet, rollDelay, (v) => `Wallet ${v}`);
      }, LINE + 2);
    }
    if (earned > 0 && !model.coinFlight) {
      this.time.delayedCall(rollDelay, () => gameAudio.play('coin'));
      this.time.delayedCall(rollDelay + ROLL_MS, () => gameAudio.play('coin', 1.15));
    }
    if (model.note) {
      const note = model.note;
      y += 4;
      line(
        (t) => {
          const text = label(this, width / 2, t + 4, note, 1, UI.accent)
            .setOrigin(0.5, 0)
            .setMaxWidth(PANEL_W - 28)
            .setCenterAlign();
          g.fillStyle(0x2a2550).fillRect(x0 + 8, t, PANEL_W - 16, text.height + 8);
          g.lineStyle(1, UI.accent).strokeRect(x0 + 8.5, t + 0.5, PANEL_W - 17, text.height + 7);
          this.children.bringToTop(text);
        },
        wrapLineCount(note, PANEL_W - 28) * 10 + 14,
      );
    }

    // A paid offer, boxed apart from the free choices below.
    if (model.offer) {
      const offer = model.offer;
      const boxH = offer.action ? 48 : 30;
      y += 6;
      line((t) => {
        g.fillStyle(0x2a2550).fillRect(x0 + 8, t, PANEL_W - 16, boxH);
        g.lineStyle(1, UI.panelHi).strokeRect(x0 + 8.5, t + 0.5, PANEL_W - 17, boxH - 1);
        label(this, width / 2, t + 5, offer.title, 1, UI.text).setOrigin(0.5, 0);
        if (offer.action) {
          if (offer.detail) {
            label(this, width / 2, t + 15, offer.detail, 1, UI.dim).setOrigin(0.5, 0);
          }
          bevelButton(
            this,
            x0 + 14,
            t + 26,
            PANEL_W - 28,
            16,
            `PAY  ${offer.cost} TO CONTINUE`,
            offer.action,
            false,
            1,
            3,
          );
        } else {
          // Not enough coins: say the price, coin icon and all.
          const text = label(this, 0, t + 16, `Costs  ${offer.cost} coins`, 1, UI.dim);
          text.setX(Math.round(width / 2 - text.width / 2));
          drawCoin(g, text.x + 5 * 6 + 3, t + 16);
        }
      }, boxH);
    }

    // Buttons: secondaries side by side, then the primary, full width, at the bottom.
    y += 8;
    const primary = model.buttons.find((b) => b.primary);
    const others = model.buttons.filter((b) => b !== primary);
    const othersTop = y;
    if (others.length > 0) y += BUTTON_H + 6;
    const primaryTop = y;
    if (primary) y += PRIMARY_H;

    // Everything is laid out from y = 0; the camera scroll centers the panel over the lawn area,
    // above the HUD's tool and boost bar (on a tablet's shape the panel reached over it).
    const hud = this.scene.get('Hud') as HudScene | null;
    const toolbar = hud?.sys.isActive() ? hud.toolbarHeight : 0;
    const panelH = y + 10;
    // Too tall to fit above the bar (an iPad's shape): the bar hides while this card is up.
    const hideBar = toolbar > 0 && panelH > height - HUD_HEIGHT - toolbar - 4;
    const room = height - HUD_HEIGHT - (hideBar ? 0 : toolbar);
    // Too tall for a short screen: leave out the extras one by one (the note, which a reveal card
    // repeats; the ribbon; the judges) until it fits.
    if (panelH > room - 4) {
      const fewer: ResultsModel | null = model.note
        ? { ...model, note: undefined }
        : model.banner
          ? { ...model, banner: undefined }
          : model.judges
            ? { ...model, judges: undefined }
            : null;
      if (fewer) {
        this.time.removeAllEvents();
        g.destroy();
        this.create(fewer);
        return;
      }
    }
    const top = Math.round(HUD_HEIGHT + Math.max(0, room - panelH) / 2);
    if (hideBar && hud) {
      hud.setToolbarHidden(true);
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        if (hud.sys.isActive()) hud.setToolbarHidden(false);
      });
    }
    navyPanel(g, x0, 0, PANEL_W, panelH);
    this.panelTop = top;
    this.cameras.main.setScroll(0, -top);
    parts.forEach((p) => p());
    if (model.cheer) this.confetti(x0, top);
    const flight = model.coinFlight;
    if (flight && net > 0) {
      this.flyCoins(
        lineSpots.filter((l) => l.coins > 0).map((l) => ({ x: x0 + PANEL_W - 20, y: l.y })),
        { x: flight.target.x, y: flight.target.y - top },
        flight.from,
        net,
        rollDelay,
        flight.onLand,
      );
    }

    const inner = PANEL_W - 20;
    const each =
      others.length > 0 ? Math.floor((inner - 6 * (others.length - 1)) / others.length) : 0;
    others.forEach((b, i) =>
      bevelButton(
        this,
        x0 + 10 + i * (each + 6),
        othersTop,
        each,
        BUTTON_H,
        b.label.toUpperCase(),
        b.action,
      ),
    );
    if (primary) {
      bevelButton(
        this,
        x0 + 10,
        primaryTop,
        inner,
        PRIMARY_H,
        primary.label.toUpperCase(),
        primary.action,
        true,
        2,
      );
    }
  }

  /**
   * GOALS, beside the stars (the stars take a tap too): what each star asked, so a missed one
   * makes sense after the lawn as well as on the level card before it.
   */
  private goalsButton(x0: number, top: number, goals: StarGoals['goals'], bits: number): void {
    const w = 38;
    const starsW = 3 * STAR_SIZE + 2 * 10;
    bevelButton(this, x0 + PANEL_W - 8 - w, top + 4, w, 13, 'GOALS', () =>
      this.showGoals(goals, bits),
    );
    this.add
      .zone(Math.round(x0 + (PANEL_W - starsW) / 2), top, starsW, STAR_SIZE)
      .setOrigin(0)
      .setInteractive({ useHandCursor: true })
      .on('pointerup', () => this.showGoals(goals, bits));
  }

  /** The star goals in a card over the results; a tap anywhere closes it. */
  private showGoals(goals: StarGoals['goals'], bits: number): void {
    const { width, height } = this.scale.gameSize;
    const mark = this.children.length;
    // Screen coordinates: the camera shows world y = -panelTop at the top of the screen.
    const sy = -this.panelTop;
    const textW = PANEL_W - 36;
    const rows = goals.map(([, text]) => wrapLineCount(text, textW) * 10 + 4);
    const cardH = 8 + 14 + rows.reduce((a, b) => a + b, 0) + 6 + 18 + 8;
    const x0 = Math.round((width - PANEL_W) / 2);
    const top = Math.round(sy + HUD_HEIGHT + (height - HUD_HEIGHT - cardH) / 2);
    // The card's own pieces, kept when it is built: confetti and flying coins leave the display
    // list while it is open, so a later slice from `mark` would miss the card (and it stuck).
    let card: Phaser.GameObjects.GameObject[] = [];
    const close = () => {
      for (const o of card) o.destroy();
      card = [];
    };
    this.add.zone(0, sy, width, height).setOrigin(0).setInteractive().on('pointerup', close);
    const g = this.add.graphics();
    g.fillStyle(0x000000, 0.5).fillRect(0, sy, width, height);
    navyPanel(g, x0, top, PANEL_W, cardH);
    label(this, width / 2, top + 8, 'STAR GOALS', 1, UI.accent).setOrigin(0.5, 0);
    let y = top + 22;
    goals.forEach(([bit, text], i) => {
      drawBigStar(g, x0 + 12, y, (bits & bit) !== 0);
      label(this, x0 + 24, y, text).setMaxWidth(textW);
      y += rows[i]!;
    });
    bevelButton(this, x0 + 10, y + 6, PANEL_W - 20, 18, 'OK', close);
    card = this.children.list.slice(mark);
    // Over the confetti and the flying coins.
    for (const o of card) (o as unknown as { setDepth: (d: number) => void }).setDepth(50);
  }

  /** The win title lands like a stamp: big, then down to size with a bounce and a flash. */
  private stampTitle(text: Phaser.GameObjects.BitmapText): void {
    if (reducedMotion()) return;
    text.setScale(2.4).setAlpha(0);
    this.tweens.add({
      targets: text,
      scale: 1,
      alpha: 1,
      duration: 380,
      ease: 'Back.easeOut',
      onComplete: () => {
        haptics.impact();
        this.cameras.main.shake(120, 0.004);
      },
    });
  }

  /** Clippings and coin glints raining over the panel. */
  private confetti(x0: number, top: number): void {
    if (reducedMotion()) return;
    const colors = [0x7ad04c, 0x4f9e3e, 0xd2f58a, UI.accent, 0xffffff];
    for (let i = 0; i < 34; i++) {
      const bit = this.add.graphics().setDepth(20);
      bit.fillStyle(colors[i % colors.length]!).fillRect(-1, -1, 2 + (i % 2), 2);
      const sx = x0 - 6 + Math.random() * (PANEL_W + 12);
      const sy = -top - 6 - Math.random() * 30;
      bit.setPosition(sx, sy).setAngle(Math.random() * 360);
      this.tweens.add({
        targets: bit,
        y: sy + 120 + Math.random() * 140,
        x: sx + (Math.random() - 0.5) * 40,
        angle: bit.angle + (Math.random() - 0.5) * 540,
        alpha: { from: 1, to: 0 },
        delay: 120 + Math.random() * 500,
        duration: 1100 + Math.random() * 700,
        ease: 'Sine.easeIn',
        onComplete: () => bit.destroy(),
      });
    }
  }

  /**
   * Coins fly from the payout lines into the HUD's coin counter, one after another along an arc,
   * and the counter ticks up as each lands (owner, 2026-10-05: winnings flying into your
   * pocket). With reduced motion the counter just updates.
   */
  private flyCoins(
    from: { x: number; y: number }[],
    to: { x: number; y: number },
    wallet: number,
    net: number,
    delay: number,
    onLand: (wallet: number | null) => void,
  ): void {
    if (reducedMotion() || from.length === 0) {
      this.time.delayedCall(delay, () => {
        onLand(wallet + net);
        onLand(null);
      });
      return;
    }
    const count = Math.max(3, Math.min(16, Math.ceil(net / 4)));
    const gap = 70;
    let landed = 0;
    for (let i = 0; i < count; i++) {
      const start = from[i % from.length]!;
      const coin = this.add.graphics().setDepth(30);
      drawCoin(coin, -3, -3);
      coin.setPosition(start.x, start.y).setVisible(false);
      const k = { t: 0 };
      const lift = 30 + (i % 4) * 10;
      const sway = (i % 2 === 0 ? -1 : 1) * (10 + (i % 3) * 6);
      this.tweens.add({
        targets: k,
        t: 1,
        delay: delay + i * gap,
        duration: 560,
        ease: 'Sine.easeIn',
        onStart: () => coin.setVisible(true),
        onUpdate: () => {
          const t = k.t;
          const x = start.x + (to.x - start.x) * t + Math.sin(t * Math.PI) * sway;
          const y = start.y + (to.y - start.y) * t - Math.sin(t * Math.PI) * lift;
          coin.setPosition(Math.round(x), Math.round(y)).setScale(1 + Math.sin(t * Math.PI) * 0.6);
        },
        onComplete: () => {
          coin.destroy();
          landed++;
          // The counter takes its share of the winnings with each coin.
          onLand(wallet + Math.round((net * landed) / count));
          gameAudio.play('coin', 0.9 + landed * 0.035);
          if (landed === count) {
            haptics.tap();
            onLand(null);
          }
        },
      });
    }
  }

  /** Counts a number up (or down) in `text` over ROLL_MS after `delay`. */
  private roll(
    text: Phaser.GameObjects.BitmapText,
    from: number,
    to: number,
    delay: number,
    format: (v: number) => string,
  ): void {
    text.setText(format(from));
    if (from === to) return;
    this.tweens.addCounter({
      from,
      to,
      duration: ROLL_MS,
      delay,
      ease: 'Cubic.easeOut',
      onUpdate: (tween) => text.setText(format(Math.round(tween.getValue() ?? to))),
      onComplete: () => text.setText(format(to)),
    });
  }

  /** A yellow ribbon that pops in under the title. */
  private drawBanner(cx: number, top: number, text: string): void {
    const w = text.length * 6 + 16;
    const g = this.add.graphics({ x: cx, y: top + 6 });
    g.fillStyle(UI.shadow).fillRect(-w / 2 + 1, -5, w, 13);
    g.fillStyle(UI.accent).fillRect(-w / 2, -6, w, 12);
    const t = label(this, cx, top + 3, text, 1, UI.panel)
      .setOrigin(0.5, 0)
      .setDropShadow(0, 0, 0, 0);
    // Centered origin on an odd width lands on a half pixel: nudge it whole.
    if (t.width % 2 === 1) t.x += 0.5;
    for (const o of [g, t]) {
      this.tweens.add({
        targets: o,
        scale: { from: 0.4, to: 1 },
        duration: 260,
        delay: 120,
        ease: 'Back.easeOut',
      });
    }
  }

  /**
   * Lawn Art's judges: three little figures behind the table who raise their scorecards one after
   * another, each with a soft chime (a 10 rings brighter).
   */
  private drawJudges(cx: number, top: number, scores: readonly number[]): void {
    const gap = 40;
    const x0 = Math.round(cx - gap);
    const coats = [0x3f6fd0, 0xc8553d, 0x4f9e3e];
    scores.forEach((score, i) => {
      const x = x0 + i * gap;
      const g = this.add.graphics();
      // The judge: a head and shoulders behind the table.
      g.fillStyle(UI.shadow).fillRect(x - 6, top + 21, 13, 9);
      g.fillStyle(coats[i % coats.length]!).fillRect(x - 6, top + 20, 12, 9);
      g.fillStyle(0xf2c9a0).fillRect(x - 3, top + 13, 6, 6);
      g.fillStyle(0x4a3326).fillRect(x - 3, top + 12, 6, 2);
      g.fillStyle(0xf4f1e6).fillRect(x - 9, top + 28, 18, 3);
      // The scorecard, raised on a stick when its turn comes.
      const card = this.add.container(x, top + 14);
      const cg = this.add.graphics();
      cg.fillStyle(0x8b5a2b).fillRect(-1, 0, 2, 8);
      cg.fillStyle(UI.shadow).fillRect(-7, -12, 15, 12);
      cg.fillStyle(0xffffff).fillRect(-8, -13, 15, 12);
      const n = label(this, 0, -11, String(score), 1, score >= 10 ? 0xd84a3a : UI.panel)
        .setOrigin(0.5, 0)
        .setDropShadow(0, 0, 0, 0);
      card.add([cg, n]);
      const delay = 900 + i * 380;
      if (reducedMotion()) {
        card.setY(top + 6);
      } else {
        card.setY(top + 26).setAlpha(0);
        this.tweens.add({
          targets: card,
          y: top + 6,
          alpha: 1,
          delay,
          duration: 260,
          ease: 'Back.easeOut',
        });
      }
      this.time.delayedCall(delay, () => gameAudio.play('star', score >= 10 ? 1.3 : 0.9 + i * 0.1));
    });
  }

  /** Three big stars; earned ones stamp in one by one, with a chime for each new one. */
  private drawStars(cx: number, top: number, bits: number, fresh: number): void {
    const gap = 10;
    const x0 = Math.round(cx - (3 * STAR_SIZE + 2 * gap) / 2);
    STAR_BITS.forEach((bit, i) => {
      const earned = (bits & bit) !== 0;
      const sx = x0 + i * (STAR_SIZE + gap);
      // Empty slot underneath, the earned star drawn on its own so it can stamp in.
      const slot = this.add.graphics();
      drawBigStar(slot, sx, top, false, STAR_SCALE);
      if (!earned) return;
      const star = this.add.graphics({ x: sx + STAR_SIZE / 2, y: top + STAR_SIZE / 2 });
      drawBigStar(star, -STAR_SIZE / 2, -STAR_SIZE / 2, true, STAR_SCALE);
      const delay = 200 + i * 220;
      star.setScale(0);
      this.tweens.add({
        targets: star,
        scale: { from: 1.8, to: 1 },
        alpha: { from: 0, to: 1 },
        duration: 220,
        delay,
        ease: 'Back.easeOut',
      });
      if ((fresh & bit) !== 0)
        this.time.delayedCall(delay, () => gameAudio.play('star', 1 + i * 0.12));
    });
  }
}
